/**
 * KingDashboard — the "throne room" for users whose role is `king`.
 *
 * Shows:
 *   - The King's crest + slot number
 *   - List of Ambassadors they've appointed (with each ambassador's
 *     direct-recruit count)
 *   - "Appoint Ambassador" form (email lookup)
 *   - Direct king-recruits (users who joined via the king's number
 *     directly, without an intermediate ambassador)
 *
 * Created Feb 12 2026 for the King/Ambassador hierarchy rollout.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Crown, Sparkles, UserPlus, Loader2, X, Users, ChevronLeft, Trash2,
  BarChart3, Activity, Globe, Megaphone, Send,
} from 'lucide-react';
import { checkAuth, authFetch } from '../utils/authUtils';
import CrestBadge from '../components/CrestBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KingDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [downline, setDownline] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appointEmail, setAppointEmail] = useState('');
  const [appointing, setAppointing] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState('');
  // Broadcast form state — Feb 12 2026 user spec: Kings can broadcast
  // to users in their own country (only).
  const [bTitle, setBTitle] = useState('');
  const [bBody, setBBody] = useState('');
  const [bTone, setBTone] = useState('info');
  const [bSending, setBSending] = useState(false);
  const [bDeleting, setBDeleting] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [dRes, aRes, brRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/me/downline`),
        authFetch(`${BACKEND_URL}/api/king/analytics?days=7&limit=30`),
        authFetch(`${BACKEND_URL}/api/king/broadcasts`),
      ]);
      if (dRes.ok) setDownline(await dRes.json());
      if (aRes.ok) setAnalytics(await aRes.json());
      if (brRes.ok) setBroadcasts(((await brRes.json()).announcements) || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await checkAuth();
        if (cancelled) return;
        if (!u || u.role !== 'king') {
          navigate('/dashboard');
          return;
        }
        setMe(u);
        await refresh();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, refresh]);

  const onAppoint = async (e) => {
    e?.preventDefault?.();
    const email = appointEmail.trim();
    if (!email) { toast.error('Enter an email'); return; }
    setAppointing(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/king/ambassador/appoint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not appoint');
        return;
      }
      toast.success(`Appointed ${email} as Ambassador #${d.ambassador_number} (${d.crest_id})`);
      setAppointEmail('');
      await refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setAppointing(false);
    }
  };

  const onRevoke = async (userId, name) => {
    if (!window.confirm(`Revoke ambassador status from ${name}?`)) return;
    setRevokeBusy(userId);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/king/ambassador/revoke/${userId}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Revoke failed');
        return;
      }
      toast.success(`Revoked. Restored role: ${d.restored_role}`);
      await refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setRevokeBusy('');
    }
  };

  const onBroadcast = async (e) => {
    e?.preventDefault?.();
    const title = bTitle.trim();
    const body = bBody.trim();
    if (!title || !body) {
      toast.error('Title and body are both required');
      return;
    }
    setBSending(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/king/broadcasts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, body, tone: bTone, audience: 'users' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Broadcast failed');
        return;
      }
      toast.success(`Broadcast sent to ${d.country}`);
      setBTitle('');
      setBBody('');
      await refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setBSending(false);
    }
  };

  const onDeleteBroadcast = async (id) => {
    if (!window.confirm('Delete this broadcast?')) return;
    setBDeleting(id);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/king/broadcasts/${id}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Could not delete');
        return;
      }
      toast.success('Broadcast deleted');
      await refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setBDeleting('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading throne room…
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="king-dashboard">
      <header className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs text-amber-200/80 hover:text-amber-100"
          data-testid="king-dashboard-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-amber-300" />
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Throne Room</h1>
        </div>
        <span className="w-6" />
      </header>

      <CrestBadge />

      {/* Edit-grant requests CTA — Feb 12 2026 user spec: writers get
          1 edit per item; Kings can grant extras. Pull-in their queue. */}
      <button
        type="button"
        onClick={() => navigate('/admin/edit-grants')}
        className="w-full rounded-2xl border border-rose-400/40 bg-rose-400/5 hover:bg-rose-400/10 p-4 flex items-center justify-between gap-3 text-left transition"
        data-testid="king-dashboard-edit-grants-btn"
      >
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-rose-400/15 grid place-items-center">
            <Crown className="w-4 h-4 text-rose-200" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-rose-200/80 font-black">Edit requests</p>
            <p className="text-sm font-bold text-white">Review writer edit requests</p>
          </div>
        </div>
        <ChevronLeft className="w-4 h-4 text-white/40 rotate-180" />
      </button>

      {/* Realm analytics — Feb 12 2026 user spec: "make analytics
          available in the profile of all Kings". Pulls /api/king/analytics
          which is scoped to the king's country. */}
      <section
        className="rounded-2xl border border-cyan-500/30 bg-slate-950/40 p-5 space-y-3"
        data-testid="king-analytics-section"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-cyan-300" />
            <h2 className="text-lg font-bold text-white">Realm Analytics</h2>
          </div>
          {analytics?.country && (
            <span className="inline-flex items-center gap-1 text-xs text-cyan-200/80 font-bold">
              <Globe className="w-3 h-3" /> {analytics.country}
            </span>
          )}
        </div>
        {!analytics ? (
          <div className="text-sm text-slate-400 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : analytics.hint ? (
          <p className="text-sm text-amber-200/80" data-testid="king-analytics-hint">
            {analytics.hint}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="king-analytics-cards">
              <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-3">
                <p className="text-[10px] uppercase tracking-widest text-cyan-200/80 font-bold">Active</p>
                <p className="text-2xl font-black text-white tabular-nums">{analytics.summary?.active_users ?? 0}</p>
                <p className="text-[10px] text-slate-400">last {analytics.summary?.window_days || 7}d</p>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                <p className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-bold">Events</p>
                <p className="text-2xl font-black text-white tabular-nums">{analytics.summary?.total_events ?? 0}</p>
              </div>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold">Logins</p>
                <p className="text-2xl font-black text-white tabular-nums">{analytics.summary?.by_kind?.login ?? 0}</p>
              </div>
              <div className="rounded-lg border border-pink-500/30 bg-pink-500/10 p-3">
                <p className="text-[10px] uppercase tracking-widest text-pink-200/80 font-bold">Joins</p>
                <p className="text-2xl font-black text-white tabular-nums">{analytics.summary?.by_kind?.register ?? 0}</p>
              </div>
            </div>
            {!!analytics.feed?.length && (
              <ol className="divide-y divide-slate-800 mt-2" data-testid="king-analytics-feed">
                {analytics.feed.slice(0, 6).map((row, i) => (
                  <li key={`${row.user_id}-${row.created_at}-${i}`} className="py-1.5 flex items-center gap-2">
                    <Activity className="w-3 h-3 text-emerald-300 shrink-0" />
                    <span className="text-xs text-white truncate flex-1">
                      <span className="font-bold">{row.user_name || row.user_id}</span>
                      <span className="text-slate-400"> — {row.label}</span>
                    </span>
                    <span className="text-[10px] text-slate-500 tabular-nums">
                      {new Date(row.created_at).toLocaleTimeString()}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </section>

      {/* Broadcast — Feb 12 2026 user spec: Kings can broadcast to
          their own country only. Body posts to /api/king/broadcasts
          which auto-scopes the country from the king's user doc. */}
      <section
        className="rounded-2xl border border-pink-500/30 bg-slate-950/40 p-5 space-y-3"
        data-testid="king-broadcast-section"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-pink-300" />
            <h2 className="text-lg font-bold text-white">Broadcast to your realm</h2>
          </div>
          {analytics?.country && (
            <span className="inline-flex items-center gap-1 text-xs text-pink-200/80 font-bold">
              <Globe className="w-3 h-3" /> {analytics.country} only
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400">
          Sent as a banner to every user whose country matches yours. They can dismiss it. The High King can see all your broadcasts in the activity feed.
        </p>
        <form onSubmit={onBroadcast} className="space-y-2">
          <input
            type="text"
            value={bTitle}
            onChange={(e) => setBTitle(e.target.value)}
            placeholder="Title (e.g. Welcome to the new chapter)"
            className="w-full rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            data-testid="king-broadcast-title"
            disabled={bSending}
            maxLength={120}
          />
          <textarea
            value={bBody}
            onChange={(e) => setBBody(e.target.value)}
            placeholder="Body — say something to your country"
            className="w-full rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500/40 resize-y min-h-[80px]"
            data-testid="king-broadcast-body"
            disabled={bSending}
            maxLength={500}
          />
          <div className="flex items-center justify-between gap-2">
            <select
              value={bTone}
              onChange={(e) => setBTone(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5"
              data-testid="king-broadcast-tone"
              disabled={bSending}
            >
              <option value="info">Info</option>
              <option value="celebration">Celebration</option>
              <option value="warning">Warning</option>
              <option value="urgent">Urgent</option>
            </select>
            <button
              type="submit"
              disabled={bSending}
              className="inline-flex items-center gap-2 rounded-lg bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold px-4 py-2 disabled:opacity-50"
              data-testid="king-broadcast-send"
            >
              {bSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send broadcast
            </button>
          </div>
        </form>

        {!!broadcasts.length && (
          <div className="space-y-1.5 pt-2 border-t border-slate-800" data-testid="king-broadcast-history">
            <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">Your past broadcasts</p>
            {broadcasts.map((b) => (
              <div
                key={b.announcement_id}
                className="rounded-lg border border-pink-500/20 bg-pink-500/5 p-2 flex items-start gap-2 text-sm"
                data-testid={`king-broadcast-row-${b.announcement_id}`}
              >
                <Megaphone className="w-3 h-3 text-pink-300 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white truncate">{b.title}</p>
                  <p className="text-xs text-white/60 truncate">{b.body}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {b.country} · {new Date(b.created_at).toLocaleString()} · {b.dismissal_count || 0} dismissed
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onDeleteBroadcast(b.announcement_id)}
                  disabled={bDeleting === b.announcement_id}
                  className="rounded-md bg-rose-500/20 hover:bg-rose-500/40 text-rose-200 px-2 py-1 disabled:opacity-50"
                  data-testid={`king-broadcast-delete-${b.announcement_id}`}
                  title="Delete broadcast"
                >
                  {bDeleting === b.announcement_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        className="rounded-2xl border border-pink-500/30 bg-slate-950/40 p-5 space-y-4"
        data-testid="king-appoint-section"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-300" />
          <h2 className="text-lg font-bold text-white">Appoint an Ambassador</h2>
        </div>
        <p className="text-xs text-slate-400">
          Ambassadors carry your crest. Their recruits, and their recruits&apos; recruits,
          will all show your <code className="text-pink-200">K-{downline?.king_number || (me?.ambassador_number)}</code> mark.
        </p>
        <form onSubmit={onAppoint} className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={appointEmail}
            onChange={(e) => setAppointEmail(e.target.value)}
            placeholder="ambassador@email"
            className="flex-1 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            data-testid="king-appoint-email"
            disabled={appointing}
          />
          <button
            type="submit"
            disabled={appointing}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold px-4 py-2 disabled:opacity-50"
            data-testid="king-appoint-submit"
          >
            {appointing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Appoint
          </button>
        </form>
      </section>

      <section
        className="rounded-2xl border border-amber-500/30 bg-slate-950/40 p-5 space-y-3"
        data-testid="king-ambassadors-section"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-300" />
            <h2 className="text-lg font-bold text-white">My Ambassadors</h2>
          </div>
          <span className="text-xs text-amber-200/80">
            {downline?.ambassadors_count || 0} active
          </span>
        </div>
        {!downline?.ambassadors?.length ? (
          <p className="text-sm text-slate-400">No ambassadors yet. Appoint your first above.</p>
        ) : (
          <div className="space-y-2">
            {downline.ambassadors.map((a) => (
              <div
                key={a.user_id}
                className="rounded-xl border border-pink-500/20 bg-pink-500/5 p-3 flex items-center gap-3"
                data-testid={`king-ambassador-row-${a.user_id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-pink-500/30 grid place-items-center shrink-0">
                  <Sparkles className="w-5 h-5 text-pink-200" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{a.name}</p>
                  <p className="text-[11px] text-pink-200/80 tabular-nums">
                    {a.crest_id || `A-${a.ambassador_number}`}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    {a.recruits_count} recruit{a.recruits_count === 1 ? '' : 's'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onRevoke(a.user_id, a.name)}
                  disabled={revokeBusy === a.user_id}
                  className="inline-flex items-center gap-1 text-xs rounded-md bg-rose-500/20 hover:bg-rose-500/40 text-rose-200 px-2.5 py-1.5 disabled:opacity-50"
                  data-testid={`king-ambassador-revoke-${a.user_id}`}
                  title="Revoke ambassador"
                >
                  {revokeBusy === a.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        className="rounded-2xl border border-cyan-500/30 bg-slate-950/40 p-5 space-y-3"
        data-testid="king-direct-recruits-section"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-300" />
            <h2 className="text-lg font-bold text-white">Direct Recruits</h2>
          </div>
          <span className="text-xs text-cyan-200/80">
            {downline?.direct_recruits_count || 0}
          </span>
        </div>
        {!downline?.direct_recruits?.length ? (
          <p className="text-sm text-slate-400">
            No direct recruits yet. Users who use your crest <code className="text-cyan-200">#{downline?.king_number}</code> as their recruiter (without going through an ambassador) will appear here.
          </p>
        ) : (
          <div className="space-y-1.5">
            {downline.direct_recruits.map((r) => (
              <div
                key={r.user_id}
                className="text-sm text-slate-200 flex items-center justify-between gap-2"
                data-testid={`king-direct-recruit-row-${r.user_id}`}
              >
                <span className="truncate">{r.name}</span>
                <span className="text-[11px] text-cyan-200/80 tabular-nums">{r.crest_id || ''}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default KingDashboard;
